import React, { useEffect, useState, createContext, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { fetchCategoryList, fetchCategoryPosts } from 'lib/core/data/blog/categoryList'
import { getFirstLoad, getFailure, getCategoryBySlug, getCategoryList, getPageInfo } from 'lib/core/data/blog/categoryList/selectors'
import { getPostBySlugs, getPost } from 'lib/core/data/blog/post/selectors'
import { PostElement } from 'lib/core/data/blog/post/types'
import { fetchPost } from 'lib/core/data/blog/post'
import { blogTagsList } from 'services/blog'
import { CustomPost } from 'data/blogPosts'

const categories: string[] = []

export const useBlogStatus = () => {
  const [fetching, setFetching] = useState(true)

  return {
    fetching,
    setFetching
  }
}

export const useBlog = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isFirstLoad = useSelector(getFirstLoad)
  const isFailure = useSelector(getFailure)
  const categoryList = useSelector(getCategoryList) ?? []
  const isFetching = !categoryList.length && !isFailure

  useEffect(() => {
    dispatch(fetchCategoryList())
  }, [
    dispatch
  ])

  useEffect(() => {
    if (categoryList.length <= 2) {
      history.push(categoryList[0]?.url_key)
    }
    categoryList?.map(category => (categories.push(category.url_key)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categoryList
  ])

  return {
    isFirstLoad,
    isFailure,
    isFetching,
    categoryList
  }
}

export const useBlogPost = (slug: string) => {
  const dispatch = useDispatch()
  const post = useSelector(getPost(slug))
  const { isFirstLoad, isFailure, data } = post ?? {}

  const {
    title,
    post_image: image,
    full_content: content,
    posted_by: author,
    published_at: publishDate,
    prev,
    next,
    tags,
    meta_title: metaTitle,
    meta_description: metaDescription
  } = data as CustomPost ?? {}

  const relatedPosts = [prev, next]

  useEffect(() => {
    dispatch(fetchPost(slug))
  }, [
    slug,
    dispatch
  ])

  return {
    title,
    image,
    content,
    author,
    metaTitle,
    metaDescription,
    publishDate,
    relatedPosts,
    isFirstLoad,
    isFailure,
    tags
  }
}

export const useBlogToolbar = (slug: string) => {
  const dispatch = useDispatch()
  const { data } = useQuery(blogTagsList)
  const tagList = data?.getBlogTagsByStore
  const { setFetching } = useBlogStatus()

  const search = (value: string) => {
    setFetching(true)
    dispatch(fetchCategoryPosts(slug, 1, { searchString: value }))
    setFetching(false)
  }

  const sortBy = (value: string) => {
    setFetching(true)
    const values = value.split('-')
    const sortValue = values[0]
    const directionValue = values[1]
    dispatch(fetchCategoryPosts(slug, 1, { sortValue, directionValue }))
    setFetching(false)
  }

  return {
    tagList,
    search,
    sortBy
  }
}

interface BlogContextInterface {
  posts?: Array<PostElement | undefined>
  loading: boolean
  search: (param: string) => void
  handlePageChange: (param: number) => void
  name?: string
  postSlugs?: string[]
  pageInfo?: Record<string, any>
  isViewAll: boolean
  categoryExists: boolean
  isSearchPage: boolean
  metaData: {
    metaTitle?: string
    metaDescription?: string
    metaKeywords?: string
  }
}

export const BlogContext = createContext<BlogContextInterface>({
  posts: [],
  loading: true,
  search: (param) => undefined,
  handlePageChange: (param) => undefined,
  name: '',
  postSlugs: [],
  pageInfo: {},
  isViewAll: false,
  categoryExists: true,
  isSearchPage: false,
  metaData: {}
})

interface Props {
  children: ReactNode
  isSofia?: boolean
}

export const BlogProvider = ({ children, isSofia }: Props) => {
  const ALL_POSTS = 'all-posts'
  const [posts, setPosts] = useState<Array<PostElement | undefined>>()
  const { pathname } = useLocation()
  const path = pathname.substring(pathname.lastIndexOf('/') + 1)
  const categoryExists = categories.includes(path)
  const isSearchPage = pathname.includes('search')
  const slug = (isSearchPage || isSofia) ? ALL_POSTS : path
  const dispatch = useDispatch()
  const category = useSelector(getCategoryBySlug(slug))
  const pageInfo = useSelector(getPageInfo(slug))

  const {
    name,
    postSlugs,
    meta_title: metaTitle,
    meta_description: metaDescription,
    meta_keywords: metaKeyword
  } = category ?? {}
  const allPosts = useSelector(getPostBySlugs(postSlugs))
  const isViewAll = slug === ALL_POSTS
  const { fetching, setFetching } = useBlogStatus()
  const { search } = useBlogToolbar(ALL_POSTS)

  const metaData = {
    metaTitle,
    metaDescription,
    metaKeyword
  }

  const handlePageChange = (e: number) => {
    async function getNewPage () {
      setFetching(true)
      window.scrollTo(0, 0)
      await dispatch(fetchCategoryPosts(slug, e))
      setFetching(false)
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    getNewPage()
  }

  useEffect(() => {
    if (isSearchPage) {
      setFetching(true)
      setPosts(undefined)
      search(path)
    } else {
      dispatch(fetchCategoryPosts(slug))
    }
    // eslint-disable-next-line
  }, [
    path,
    dispatch
  ])

  useEffect(() => {
    if (category && pageInfo?.pageSize && pageInfo?.pageSize > 0) {
      setFetching(false)
    }
    // eslint-disable-next-line
  }, [
    category,
    setFetching,
    posts,
    pageInfo
  ])

  useEffect(() => {
    if (postSlugs) {
      setPosts(allPosts)
    }
    // eslint-disable-next-line
  }, [postSlugs])

  return (
    <BlogContext.Provider
      value={{
        posts,
        loading: fetching,
        search,
        handlePageChange,
        name,
        postSlugs,
        pageInfo,
        isViewAll,
        categoryExists,
        isSearchPage,
        metaData
      }}
    >
      {children}
    </BlogContext.Provider>
  )
}

export const useBlogContext = () => {
  const context = React.useContext(BlogContext)
  if (context === undefined) {
    throw new Error('useBlog must be used within a BlogProvider')
  }
  return context
}
